import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import type { UppFloorPlanModel } from '../plan/floor-plan-model';
import type { UppFloorPlanPreferences } from '../plan/floor-plan-preferences';
import type { UppFloorPlanExportPngOptions } from '../export/upp-floor-plan-export-png';
import type { UppKonvaFloorPlanGroup } from '../presentation/upp-konva-floor-plan-group';
import type { UppKonvaShapeHandle } from '../presentation/upp-konva-shape-handle';
import type { UppKonvaFloorPlanModel } from './upp-konva-floor-plan-model';

/**
 * **Internal** bridge between {@link UppKonvaFloorPlanModel} and the host **`upp-konva`** / stage / tests.
 * English comment normalized.
 */
export interface UppKonvaSceneModelHost {
  readonly sceneModel: UppFloorPlanModel;
  readonly presentationPreferences: UppFloorPlanPreferences;
  setPresentationPreferences(partial: Partial<UppFloorPlanPreferences> | null | undefined): void;
  onRefresh(fn: (() => void) | null): void;
  bindFloorRefresh(fn: (() => void) | null): void;
  bindExportToPng(fn: ((options?: UppFloorPlanExportPngOptions) => Promise<Blob | null>) | null): void;
  notifyGrouped(group: UppKonvaFloorPlanGroup | null): void;
  addItem(element: UppFloorPlanElement): UppKonvaShapeHandle;
  delItem(item: UppKonvaShapeHandle): boolean;
  removeElementByUuid(uuid: string): boolean;
  setShapesFromElements(elements: readonly UppFloorPlanElement[]): void;
  readonly modelVersion: number;
  readonly vertexPreviewAnchorId: string | null;
  onRoomBackgroundDoubleClickCm(xCm: number, yCm: number): string | null;
  onRoomBackgroundSingleClick(): void;
  onVertexDoubleClick(vertexUuid: string): void;
  addVertexAtRoomCentre(): string | null;
}

const _hostByModel = new WeakMap<UppKonvaFloorPlanModel, UppKonvaSceneModelHost>();

export function registerUppKonvaSceneModelHost(
  model: UppKonvaFloorPlanModel,
  host: UppKonvaSceneModelHost,
): void {
  _hostByModel.set(model, host);
}

/** Retrieves the host instance wired to the model (used by the stage for selection, refresh, and geometry). */
export function uppKonvaSceneModelHost(model: UppKonvaFloorPlanModel): UppKonvaSceneModelHost {
  const h = _hostByModel.get(model);
  if (!h) {
    throw new Error('uppKonvaSceneModelHost: model is not a registered UppKonvaFloorPlanModel instance');
  }
  return h;
}
